import React, { useEffect, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { useCompany, useSnackbar } from "src/providers"
import { getContact, putContact, ContactPerson } from "src/services/api/companyGroups"
import { LabeledField, SectionHeader } from "./companyInfoComponents"

const defaultContact: ContactPerson = { name: "", surname: "", mail: "", phone: "" }
const toContact = (prev: ContactPerson | null, updates: Partial<ContactPerson>): ContactPerson =>
  ({ ...(prev ?? defaultContact), ...updates })

const isBlank = (s: string | null | undefined) => !s || !String(s).trim()

export const InvoicingContactPanel = () => {
  const { companyId } = useCompany()
  const { snackbarShowMessage } = useSnackbar()
  const [contact, setContact] = useState<ContactPerson | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getContact(companyId)
      .then(setContact)
      .catch(() => setContact(null))
      .finally(() => setLoading(false))
  }, [companyId])

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    )
  }

  const errors = {
    name: isBlank(contact?.name),
    surname: isBlank(contact?.surname),
    mail: isBlank(contact?.mail),
    phone: isBlank(contact?.phone)
  }
  const saveDisabled = Object.values(errors).some(Boolean)

  const onSave = () => {
    if (!companyId || !contact || saveDisabled) return
    putContact(companyId, contact)
      .then(() => snackbarShowMessage("Contact updated successfully", "success"))
      .catch(() => snackbarShowMessage("Failed to update contact", "error"))
  }

  return (
    <Box
      sx={{
        borderRadius: "40px",
        backgroundColor: "#D5F5FF",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box sx={{ px: 6, pt: 7, pb: 2, borderBottom: "1px solid #A4E9FF" }}>
        <SectionHeader
          title="Invoicing Contact"
          onEdit={onSave}
          editDisabled={saveDisabled}
        />
      </Box>
      <Stack
        spacing={2}
        sx={{
          px: 6,
          pb: 4,
          "& .MuiOutlinedInput-root": { backgroundColor: "common.white" },
          justifyContent: "space-evenly",
          flexGrow: 1
        }}
      >
        <LabeledField
          label="First Name *"
          value={contact?.name ?? ""}
          error={errors.name}
          helperText={errors.name ? "Required" : undefined}
          onChange={(e) => setContact(toContact(contact, { name: e.target.value }))}
        />
        <LabeledField
          label="Last Name *"
          value={contact?.surname ?? ""}
          error={errors.surname}
          helperText={errors.surname ? "Required" : undefined}
          onChange={(e) => setContact(toContact(contact, { surname: e.target.value }))}
        />
        <LabeledField
          label="E-mail *"
          value={contact?.mail ?? ""}
          error={errors.mail}
          helperText={errors.mail ? "Required" : undefined}
          onChange={(e) => setContact(toContact(contact, { mail: e.target.value }))}
        />
        <LabeledField
          label="Phone number *"
          value={contact?.phone ?? ""}
          error={errors.phone}
          helperText={errors.phone ? "Required" : undefined}
          onChange={(e) => setContact(toContact(contact, { phone: e.target.value }))}
        />
      </Stack>
    </Box>
  )
}
